import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "taven_cart";

export const CartProvider = ({ children }) => {
  const { user, authApi } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
  });
  const [checkingOut, setCheckingOut] = useState(false);
  const trackTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Abandoned-cart tracking for signed-in customers (email known). Debounced.
  useEffect(() => {
    if (!user || items.length === 0) return;
    clearTimeout(trackTimer.current);
    trackTimer.current = setTimeout(() => {
      const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
      authApi.post("/cart/track", {
        items: items.map((i) => ({ handle: i.handle, title: i.title, image: i.image, price: i.price, quantity: i.quantity })),
        subtotal,
      }).catch(() => {});
    }, 2500);
    return () => clearTimeout(trackTimer.current);
  }, [items, user, authApi]);

  const addItem = useCallback((product, quantity = 1) => {
    if (!product?.defaultVariantId) return false;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.variantId === product.defaultVariantId);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, {
        variantId: product.defaultVariantId,
        handle: product.handle,
        title: product.title,
        image: product.image,
        price: product.price,
        currency: product.currency,
        quantity,
      }];
    });
    return true;
  }, []);

  const updateQty = useCallback((variantId, quantity) => {
    setItems((prev) => prev.map((i) => (i.variantId === variantId ? { ...i, quantity: Math.max(1, quantity) } : i)));
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((i) => i.variantId !== variantId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  const checkout = useCallback(async (attribution = null, linesOverride = null) => {
    const lines = linesOverride || items.map((i) => ({ variantId: i.variantId, quantity: i.quantity }));
    if (!lines.length) return null;
    setCheckingOut(true);
    try {
      let code = "";
      try { code = localStorage.getItem("taven_discount_code") || ""; } catch { /* ignore */ }
      const { data } = await api.post("/shop/checkout", {
        lines,
        discount_code: code || undefined,
        attribution: attribution || undefined,
      });
      if (user) authApi.post("/cart/converted").catch(() => {});
      // Server applied the TAVEN products-only code to the cart; also append
      // the ?discount= param so the hosted checkout shows it pre-applied.
      let url = data.checkoutUrl;
      if (code && url) url += (url.includes("?") ? "&" : "?") + "discount=" + encodeURIComponent(code);
      return url;
    } finally {
      setCheckingOut(false);
    }
  }, [items, user, authApi]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, count, subtotal, checkout, checkingOut }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
